// useFeatureFlag — resolves the runtime status of a feature for the current
// company. Combines two sources, in order of precedence:
//   1. `company.feature_flags[id]` from the backend (per-company overrides)
//   2. The static `FEATURE_REGISTRY` declared in `constants/featureRegistry.ts`
//   3. `'available'` (no entry → feature is on by default)
//
// Pages that ship partially can guard their content with this hook:
//
//   const flag = useFeatureFlag('crm.opportunities');
//   if (flag.status !== 'available') {
//     return <FeatureStatus kind={flag.status} eta={flag.eta} variant="page" />;
//   }
//
// Mutations that hit a backend route returning `FEATURE_*` codes don't need
// this hook — `toastApiError()` already renders the right message via
// `resolveErrorMessage()`.

import { useApp } from '@/components/hooks/useApp';
import { FEATURE_REGISTRY, type FeatureFlag } from '@/constants/featureRegistry';

const AVAILABLE: FeatureFlag = { status: 'available' };

export function useFeatureFlag(featureId: string): FeatureFlag {
  const { company } = useApp();

  // Per-company overrides arrive as a JSON column in the company row. We treat
  // it as opaque and only read fields we recognize, never blindly forwarding.
  const override = (company as { feature_flags?: Record<string, FeatureFlag> } | null)?.feature_flags?.[featureId];
  if (override && typeof override === 'object' && override.status) {
    return override;
  }

  return FEATURE_REGISTRY[featureId] ?? AVAILABLE;
}
